import React, { useState, useRef, useEffect } from "react";
import { Menu, Button, MenuItem, Input } from "@showitinc/showit-wc";

import UIActions from "../../actions/UIActions";
import useStoreState from "../../components/providers/useStoreState";
import DesignViewStore from "../../stores/DesignViewStore";
import { INPUT_TYPE_NUMBER } from "../../constants/ElementConstants";
import ActiveBlockContainerStore from "../../stores/ActiveBlockContainerStore.mjs";
import styles from "./Toolbar.module.css";

const ZOOM_LEVELS = [
  { value: 2, label: "200%" },
  { value: 1.75, label: "175%" },
  { value: 1.5, label: "150%" },
  { value: 1.25, label: "125%" },
  { divider: true },
  { value: 1, label: "100%" },
  { divider: true },
  { value: 0.75, label: "75%" },
  { value: 0.5, label: "50%" },
  { value: 0.25, label: "25%" },
  { divider: true },
];

const ViewZoomMenu = ({ blockContainer }) => {
  const stageState = useStoreState(DesignViewStore, DesignViewStore.getStageSettings);
  const layout = blockContainer.get("layout");
  const currentZoom = stageState.getIn([layout, "zoom"], 1);
  const currentZoomDisplay = Math.round(currentZoom * 100);
  const [newManualZoom, setNewManualZoom] = useState(currentZoomDisplay);
  const zoomInputRef = useRef(null);

  // Update manual zoom input when currentZoom changes
  useEffect(() => {
    setNewManualZoom(currentZoomDisplay);
  }, [currentZoomDisplay]);

  // Zoom is applied from the input event, never from state: an effect on
  // newManualZoom echoed every zoom change back to the store a render late,
  // overwriting newer zooms mid-gesture with the previous one.
  const _zoomTo = (zoomTarget) => {
    UIActions.setZoom(zoomTarget, layout);
    setNewManualZoom(Math.round(zoomTarget * 100));
  };

  const _zoomToWidth = () => {
    const blockCont = ActiveBlockContainerStore.getContainer();
    const currentLayout = blockCont.get("layout");
    UIActions.zoomToWidth(currentLayout, blockCont);
  };

  return (
    <Menu variant="icon" shift="left" py="lg" className={styles["view-menu"]}>
      <Button
        id="view-zoom-menu-button"
        className={styles["view-menu-button"]}
        icon-suffix="chevronDown"
        slot="trigger-element"
        popovertarget="menu"
      >
        {currentZoomDisplay}%
      </Button>

      <div className={styles["zoom-menu-content"]}>
        <div className={styles["zoom-menu-scrollable"]}>
          <MenuItem keepOpen className={styles["menu-item"]}>
            <Input
              className={styles["zoom-input"]}
              type={INPUT_TYPE_NUMBER}
              ref={zoomInputRef}
              hideLabel
              value={newManualZoom}
              min={10}
              max={500}
              units="%"
              onSIInput={(e) => _zoomTo(e.detail.value / 100)}
            />
          </MenuItem>
          <MenuItem divider />

          {ZOOM_LEVELS.map((item, index) =>
            item.divider ? (
              <MenuItem key={`divider-${index}`} divider />
            ) : (
              <MenuItem
                className={styles["menu-item"]}
                key={`zoom-${item.value}`}
                keepOpen
                onClick={() => _zoomTo(item.value)}
              >
                {item.label}
              </MenuItem>
            )
          )}
        </div>

        <MenuItem keepOpen onClick={_zoomToWidth} className={styles["menu-item"]}>
          Fit to Width
        </MenuItem>
      </div>
    </Menu>
  );
};

export default ViewZoomMenu;
